/**
 * Top bar + side navigation + content outlet. All authenticated
 * pages render inside it via React Router's `<Outlet />`.
 *
 * The nav items drive the domain ordering; feature flags can later
 * hide individual entries without reshuffling.
 */

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Collapse,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { NavLink, Outlet } from 'react-router-dom';

import { useAuth } from '@/auth/AuthContext';
import { ErrorBoundary } from '@/components/feedback';
import { brand, nav } from '@/i18n/messages';
import { LanguageMenu } from './LanguageMenu';

const DRAWER_WIDTH = 240;
/// Round-19 §19: collapsed-rail width on desktop. Just wide enough
/// for the icon + a hover affordance; the labels disappear.
const DRAWER_COLLAPSED_WIDTH = 64;
const DESKTOP_COLLAPSE_STORAGE_KEY = 'relay.layout.sidebarCollapsed';

/**
 * Realm roles that the Relay side-nav branches on.
 *
 * Old Haken drove the home-page layout off an `isUserHakenStaff`
 * token flag; Relay splits the equivalent into named roles
 * (`agency_admin`, `platform_admin`, `agency_staff`,
 * `worker_self_service`) and hides nav entries per-role. A principal
 * with both an admin role and `worker_self_service` is treated as
 * an admin — `workerSelfOnly` below evaluates to `false` for them.
 */
// Every non-self-service tenant role is treated as "admin" for
// navigation purposes — `agency_editor` is write-capable (see
// CAN_WRITE_WORKER) and `agency_staff` is read-only, but both still
// need the full sidebar. Missing `agency_editor` here is why editors
// could log in but saw an empty left panel.
const ADMIN_ROLES = [
  'agency_admin',
  'agency_editor',
  'platform_admin',
  'agency_staff',
] as const;

interface NavLeaf {
  to: string;
  label: typeof nav[keyof typeof nav];
  /**
   * Roles allowed to see this entry. `undefined` = every authenticated
   * role (including self-service).
   */
  allowedRoles?: readonly string[];
  /**
   * When true, the entry is only shown if the user has the listed
   * role AND does not have any admin/staff role. Used for
   * self-service-specific affordances (e.g. "My profile").
   */
  selfServiceOnly?: boolean;
  /**
   * v0.0.66.2 — also gated on a per-user feature flag from
   * `user_feature_overrides`. When the override blocks the user, the
   * entry vanishes from the nav so they never land on an empty page.
   */
  requiredFeature?: string;
}

interface NavGroup {
  /** Synthetic key — used as collapse state handle + pathPrefix match. */
  key: string;
  label: typeof nav[keyof typeof nav];
  /** Auto-expand when the current URL starts with this prefix. */
  pathPrefix: string;
  allowedRoles?: readonly string[];
  children: NavLeaf[];
}

type NavItem = NavLeaf | NavGroup;

function isGroup(item: NavItem): item is NavGroup {
  return 'children' in item;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: nav.home },
  {
    to: '/me/profile',
    label: nav.selfProfile,
    allowedRoles: ['worker_self_service'],
    selfServiceOnly: true,
  },
  { to: '/dashboard', label: nav.dashboard, allowedRoles: ADMIN_ROLES },
  {
    // "Candidate" dropdown — matches the 5-screen menu from old Haken's
    // Candidate.md spec. URL prefix stays `/workers` (no backend
    // rename); only the visible label changes.
    key: 'candidate',
    label: nav.candidate,
    pathPrefix: '/workers',
    allowedRoles: ADMIN_ROLES,
    children: [
      { to: '/workers/new', label: nav.candidateRegister },
      { to: '/workers', label: nav.candidateList },
      { to: '/workers/scheduled-interviews', label: nav.candidateScheduled },
      { to: '/workers/search', label: nav.candidateSearch },
      { to: '/workers/unified', label: nav.candidateUnified },
    ],
  },
  {
    key: 'employees',
    label: nav.employees,
    pathPrefix: '/employees',
    allowedRoles: ADMIN_ROLES,
    children: [
      { to: '/employees', label: nav.employeeList },
      { to: '/employees/former', label: nav.employeeFormer },
      { to: '/employees/search', label: nav.employeeSearch },
      { to: '/leave/former', label: nav.employeeFormerLeave },
    ],
  },
  {
    key: 'companies',
    label: nav.companies,
    pathPrefix: '/companies',
    allowedRoles: ADMIN_ROLES,
    children: [
      { to: '/companies/new', label: nav.companyRegister },
      { to: '/companies', label: nav.companyList },
    ],
  },
  {
    key: 'contracts',
    label: nav.contracts,
    pathPrefix: '/contract/worker',
    allowedRoles: ADMIN_ROLES,
    children: [
      { to: '/contracts/new?contract_kind=worker', label: nav.contractRegister },
      { to: '/contracts?contract_kind=worker', label: nav.contractList },
      { to: '/contracts/templates?kind=contract_worker', label: nav.contractTemplates },
    ],
  },
  {
    key: 'companyContracts',
    label: nav.companyContracts,
    pathPrefix: '/contract/company',
    allowedRoles: ADMIN_ROLES,
    children: [
      { to: '/contracts/new?contract_kind=company', label: nav.companyContractRegister },
      { to: '/contracts?contract_kind=company', label: nav.companyContractList },
      { to: '/contracts/templates?kind=contract_company', label: nav.companyContractTemplates },
    ],
  },
  {
    key: 'vacancies',
    label: nav.vacancies,
    pathPrefix: '/vacancies',
    allowedRoles: ADMIN_ROLES,
    children: [
      { to: '/vacancies/new', label: nav.vacancyRegister },
      { to: '/vacancies', label: nav.vacancyList },
    ],
  },
  { to: '/leave', label: nav.leave, allowedRoles: ADMIN_ROLES },
  { to: '/leave/inbox', label: nav.leaveInbox, allowedRoles: ADMIN_ROLES },
  {
    to: '/reports',
    label: nav.reports,
    allowedRoles: ADMIN_ROLES,
    requiredFeature: 'reports',
  },
  { to: '/settings', label: nav.settings, allowedRoles: ADMIN_ROLES },
];

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Round-19 §19: persisted desktop sidebar collapse. Off by default —
  // we don't surprise existing users on first login post-merge.
  const [desktopCollapsed, setDesktopCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(DESKTOP_COLLAPSE_STORAGE_KEY) === '1';
  });
  const toggleDesktop = () => {
    setDesktopCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(DESKTOP_COLLAPSE_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* localStorage may be disabled in some browsers/incognito modes */
      }
      return next;
    });
  };
  const desktopWidth = desktopCollapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH;
  const { tenant, signOut, hasRole, hasFeature } = useAuth();
  const location = useLocation();

  const visibleNavItems = NAV_ITEMS.filter((item) => {
    if (item.allowedRoles && !hasRole(...item.allowedRoles)) return false;
    if (!isGroup(item) && item.selfServiceOnly && hasRole(...ADMIN_ROLES)) return false;
    if (!isGroup(item) && item.requiredFeature && !hasFeature(item.requiredFeature)) {
      return false;
    }
    return true;
  });

  // Auto-expand any group whose `pathPrefix` matches the current URL.
  // Users can toggle manually after that (state lives in React).
  const defaultOpenKeys = useMemo(
    () =>
      NAV_ITEMS.filter(isGroup)
        .filter((g) => location.pathname.startsWith(g.pathPrefix))
        .map((g) => g.key),
    [location.pathname],
  );
  const [openGroups, setOpenGroups] = useState<string[]>(defaultOpenKeys);
  const toggleGroup = (key: string) =>
    setOpenGroups((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  const drawerContent = (
    <Box role="navigation" aria-label="プライマリ">
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
          <FormattedMessage {...brand.productName} />
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {visibleNavItems.map((item) =>
          isGroup(item) ? (
            <Box key={item.key}>
              <ListItemButton onClick={() => toggleGroup(item.key)}>
                <ListItemText primary={<FormattedMessage {...item.label} />} />
                {openGroups.includes(item.key) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ListItemButton>
              <Collapse in={openGroups.includes(item.key)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItemButton
                      key={child.to}
                      component={NavLink}
                      to={child.to}
                      end
                      sx={{
                        pl: 4,
                        '&.active': { bgcolor: 'action.selected' },
                      }}
                    >
                      <ListItemText primary={<FormattedMessage {...child.label} />} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          ) : (
            <ListItemButton
              key={item.to}
              component={NavLink}
              to={item.to}
              end={item.to === '/'}
              sx={{
                '&.active': { bgcolor: 'action.selected' },
              }}
            >
              <ListItemText primary={<FormattedMessage {...item.label} />} />
            </ListItemButton>
          ),
        )}
      </List>
      <Divider />
      <List>
        <ListItemButton onClick={signOut}>
          <ListItemText primary={<FormattedMessage id="common.signOut" defaultMessage="Sign out" />} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${desktopWidth}px)` },
          ml: { sm: `${desktopWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="メニューを開く"
            edge="start"
            onClick={() => setMobileOpen((v) => !v)}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {/* Round-19 §19: desktop sidebar collapse toggle. */}
          <IconButton
            color="inherit"
            aria-label={desktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            edge="start"
            onClick={toggleDesktop}
            sx={{ mr: 2, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            {tenant?.name ?? ' '}
          </Typography>
          <LanguageMenu />
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: desktopWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopWidth,
              overflowX: 'hidden',
              transition: (theme) => theme.transitions.create('width', {
                duration: theme.transitions.duration.shortest,
              }),
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${desktopWidth}px)` },
        }}
      >
        <Toolbar />
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
  );
}
